import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';
import { UserSelector } from './UserSelector';
import { DriverSelector } from './DriverSelector';

const STATUS_OPTIONS = [
  { value: '',            label: 'Todos los estados de paquete' },
  { value: 'in_warehouse', label: 'En Bodega' },
  { value: 'in_fastmax',   label: 'En FastMax' },
  { value: 'with_driver',  label: 'Con Conductor' },
  { value: 'delivered',    label: 'Entregado' },
  { value: 'returned',     label: 'Devuelto' },
];

const DELIVERY_STATUS_OPTIONS = [
  { value: '',           label: 'Todos los estados de entrega' },
  { value: 'entregado',  label: 'Entregado' },
  { value: 'cancelado',  label: 'Cancelado' },
  { value: 'rechazado',  label: 'Rechazado' },
  { value: 'pending',    label: 'Pendiente' },
  { value: 'encamino',   label: 'En Camino' },
  { value: 'postergado', label: 'Postergado' },
];

/**
 * PackageFilters
 *
 * Filter bar for the package table.
 * Calls applyFilter() from the shared hook on each change.
 * Shows an "active filters" count badge when filters are applied.
 */
export function PackageFilters({ filters, applyFilter, clearFilters, users, usersLoading, activeUsersOnly, setActiveUsersOnly, requestUsers, onUserSearch, drivers, driversLoading, driversError, requestDrivers }) {
  const [expanded, setExpanded] = useState(false);

  // Count how many non-default filters are active
  const activeCount = [
    filters.status,
    filters.delivery_status,
    filters.district,
    filters.user_id,
    filters.provider_id,
    filters.driver_ids?.length,
    filters.date_from,
    filters.date_to,
    filters.delivery_date,
    filters.fulfillment_state,
  ].filter(Boolean).length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* Filter toggle header */}
      <button
        onClick={() => { if (!expanded) { requestUsers(); requestDrivers(); } setExpanded(prev => !prev); }}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <Filter size={16} className="text-gray-500" />
        <span className="font-montserrat font-semibold text-sm text-gray-700 flex-1">
          Filtros
        </span>
        {activeCount > 0 && (
          <span className="bg-brand-primary text-white text-xs font-montserrat font-bold px-2 py-0.5 rounded-full">
            {activeCount}
          </span>
        )}
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Filter fields */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* User selector */}
            <div className="space-y-1 relative z-10">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Usuario
              </label>
              <UserSelector
                onSearch={onUserSearch}
                users={users}
                usersLoading={usersLoading}
                activeUsersOnly={activeUsersOnly}
                setActiveUsersOnly={setActiveUsersOnly}
                selectedUserId={filters.user_id}
                selectedProviderId={filters.provider_id}
                onChange={(item) => {
                  if (item.type === 'provider') {
                    applyFilter({ provider_id: item.id, user_id: '' });
                  } else {
                    applyFilter({ user_id: item.id, provider_id: '' });
                  }
                }}
              />
            </div>

            {/* Assigned delivery driver(s) */}
            <div className="space-y-1 relative z-10">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Conductor
              </label>
              <DriverSelector
                drivers={drivers}
                loading={driversLoading}
                error={driversError}
                selectedIds={filters.driver_ids}
                onChange={driver_ids => applyFilter({ driver_ids })}
              />
            </div>

            {/* Package status select */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Estado paquete
              </label>
              <select
                value={filters.status}
                onChange={e => applyFilter({ status: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-primary"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Delivery status select */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Estado entrega
              </label>
              <select
                value={filters.delivery_status}
                onChange={e => applyFilter({ delivery_status: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-primary"
              >
                {DELIVERY_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* District input */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Distrito
              </label>
              <input
                type="text"
                value={filters.district}
                onChange={e => applyFilter({ district: e.target.value })}
                placeholder="Ej: Miraflores"
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Date from */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Desde
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={e => applyFilter({ date_from: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Date to */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Hasta
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={e => applyFilter({ date_to: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>

            {/* Delivery date */}
            <div className="space-y-1">
              <label className="text-xs font-montserrat font-semibold text-gray-500 uppercase tracking-wide">
                Fecha entrega
              </label>
              <input
                type="date"
                value={filters.delivery_date}
                onChange={e => applyFilter({ delivery_date: e.target.value })}
                className="w-full text-sm font-montserrat border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-primary"
              />
            </div>
          </div>

          {/* Clear filters */}
          {activeCount > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-sm font-montserrat font-semibold text-red-500 hover:text-red-700 transition-colors"
              >
                <X size={14} />
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
